import { Toast } from 'antd-mobile';
import {
  IOpenAdminVocabularyPreview,
  IOpenDeerDirectory,
  IOfflineContentCommand,
  IOpenExternalTeacherLessonVocabulary,
  IOpenExternalTeacherLessonPhonicsPractice,
  IOpenExternalTeacherLessonWordGame,
  IOpenPageForDeerPictureBookModel,
  IOpenPointBookNativePreview,
} from '../typings/js_bridge';
import { isApp } from './EnvUtils';

type NativeMessageMethod = {
  postMessage: (args?: any) => void;
};

type PreviewRecord = Record<string, unknown>;

function isPreviewRecord(value: unknown): value is PreviewRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function normalizePreviewWord(
  value: unknown,
  pageStartTimeMs: number | null,
): unknown {
  if (!isPreviewRecord(value)) {
    return value;
  }

  const word = { ...value };
  const startTimeMs = isFiniteNumber(word.startTimeMs)
    ? word.startTimeMs
    : null;
  const endTimeMs = isFiniteNumber(word.endTimeMs)
    ? word.endTimeMs
    : null;

  if (startTimeMs != null) {
    if (!isFiniteNumber(word.startTimeMsInFullAudio)) {
      word.startTimeMsInFullAudio = startTimeMs;
    }
    if (pageStartTimeMs != null) {
      word.realMediaStartTimeMs = pageStartTimeMs;
      const relativeStartTimeMs = startTimeMs - pageStartTimeMs;
      word.startTimeMs = relativeStartTimeMs < 0 ? null : relativeStartTimeMs;
    }
  }
  if (endTimeMs != null) {
    if (!isFiniteNumber(word.endTimeMsInFullAudio)) {
      word.endTimeMsInFullAudio = endTimeMs;
    }
    if (pageStartTimeMs != null) {
      const relativeEndTimeMs = endTimeMs - pageStartTimeMs;
      word.endTimeMs = relativeEndTimeMs <= 0 ? null : relativeEndTimeMs;
    }
  }

  return word;
}

function buildPreviewSentenceWords(
  sentenceText: string,
  pageWords: PreviewRecord[],
): PreviewRecord[] {
  const sentenceLowerCase = sentenceText.toLocaleLowerCase();
  const result: PreviewRecord[] = [];
  let cursor = 0;
  let previousStartTimeMs: unknown = 0;
  let previousEndTimeMs: unknown = 0;

  pageWords.forEach((word) => {
    const wordText = typeof word.wordEn === 'string' ? word.wordEn : '';
    if (!wordText) {
      return;
    }
    const wordIndex = sentenceLowerCase.indexOf(
      wordText.toLocaleLowerCase(),
      cursor,
    );
    if (wordIndex < 0) {
      return;
    }
    if (wordIndex > cursor) {
      result.push({
        wordEn: sentenceText.slice(cursor, wordIndex),
        startTimeMs: previousStartTimeMs ?? word.startTimeMs ?? 0,
        endTimeMs: previousEndTimeMs ?? word.startTimeMs ?? 0,
      });
    }
    result.push({ ...word });
    cursor = wordIndex + wordText.length;
    previousStartTimeMs = word.startTimeMs;
    previousEndTimeMs = word.endTimeMs;
  });

  if (cursor < sentenceText.length) {
    result.push({
      wordEn: sentenceText.slice(cursor),
      startTimeMs: previousStartTimeMs ?? 0,
      endTimeMs: previousEndTimeMs ?? 0,
    });
  }

  return result;
}

function normalizePreviewSentence(
  value: unknown,
  index: number,
  page: PreviewRecord,
  model: PreviewRecord,
  normalizedPageWords: PreviewRecord[],
  sentenceCount: number,
): unknown {
  if (!isPreviewRecord(value)) {
    return value;
  }

  const sentence = { ...value };
  const pageStartTimeMs = isFiniteNumber(page.startTimeMs)
    ? page.startTimeMs
    : null;
  const startTimeMs = isFiniteNumber(sentence.startTimeMs)
    ? sentence.startTimeMs
    : null;
  const endTimeMs = isFiniteNumber(sentence.endTimeMs)
    ? sentence.endTimeMs
    : null;

  if (startTimeMs != null) {
    sentence.startTimeMsInFullAudio = isFiniteNumber(
      sentence.startTimeMsInFullAudio,
    ) ? sentence.startTimeMsInFullAudio : startTimeMs;
    if (pageStartTimeMs != null) {
      sentence.startTimeMs = Math.max(0, startTimeMs - pageStartTimeMs);
    }
  }
  if (endTimeMs != null) {
    sentence.endTimeMsInFullAudio = isFiniteNumber(
      sentence.endTimeMsInFullAudio,
    ) ? sentence.endTimeMsInFullAudio : endTimeMs;
    if (pageStartTimeMs != null) {
      sentence.endTimeMs = Math.max(0, endTimeMs - pageStartTimeMs);
    }
  }

  sentence.pictureBookId = model.id;
  sentence.picturePageId = page.id;
  sentence.inSentenceListIndex = index;
  sentence.pictureChildShowType = model.childShowType;
  if (typeof sentence.mediaUrl !== 'string' && typeof page.mediaUrl === 'string') {
    sentence.mediaUrl = page.mediaUrl;
  }

  const sentenceGroup = sentence.group;
  const sentenceWords = normalizedPageWords.filter((word) => (
    sentenceCount === 1 || word.group === sentenceGroup
  ));
  if (typeof sentence.en === 'string' && sentenceWords.length > 0) {
    sentence.words = buildPreviewSentenceWords(sentence.en, sentenceWords);
  }

  return sentence;
}

/**
 * 正式绘本详情在 Flutter 内会先把长音频的绝对时间转换成单页相对时间，
 * 并为字幕补齐可点击的单词节点。原生制作预览绕过了那条详情加载链路，
 * 因此在发送桥接消息前做同等的最小整理。
 */
export function normalizePointBookNativePreview(
  data: IOpenPointBookNativePreview,
): IOpenPointBookNativePreview {
  if (!isPreviewRecord(data.model)) {
    return data;
  }

  const model = { ...data.model };
  if (!Array.isArray(model.items)) {
    return { ...data, model };
  }

  model.items = model.items.map((value) => {
    if (!isPreviewRecord(value)) {
      return value;
    }

    const page = { ...value };
    const pageStartTimeMs = isFiniteNumber(page.startTimeMs)
      ? page.startTimeMs
      : null;
    if (pageStartTimeMs != null && !isFiniteNumber(page.startTimeMsInFullAudio)) {
      page.startTimeMsInFullAudio = pageStartTimeMs;
    }
    if (isFiniteNumber(page.endTimeMs) && !isFiniteNumber(page.endTimeMsInFullAudio)) {
      page.endTimeMsInFullAudio = page.endTimeMs;
    }

    const normalizedWords = Array.isArray(page.words)
      ? page.words.map((word) => normalizePreviewWord(word, pageStartTimeMs))
      : [];
    if (Array.isArray(page.words)) {
      page.words = normalizedWords;
    }
    if (Array.isArray(page.contentEnSplitWords)) {
      page.contentEnSplitWords = page.contentEnSplitWords.map(
        (word) => normalizePreviewWord(word, pageStartTimeMs),
      );
    }
    if (Array.isArray(page.sentences)) {
      const sentenceCount = page.sentences.length;
      const normalizedPageWords = normalizedWords.filter(isPreviewRecord);
      page.sentences = page.sentences.map((sentence, index) => (
        normalizePreviewSentence(
          sentence,
          index,
          page,
          model,
          normalizedPageWords,
          sentenceCount,
        )
      ));
    }

    return page;
  });

  return { ...data, model };
}

function getArkWebGlobalMethod(methodName: string): NativeMessageMethod | undefined {
  try {
    if (methodName === 'buyVip' && typeof buyVip !== 'undefined') {
      return buyVip;
    }
    if (methodName === 'wxPay' && typeof wxPay !== 'undefined') {
      return wxPay;
    }
    if (
      methodName === 'checkWechatInstalled' &&
      typeof checkWechatInstalled !== 'undefined'
    ) {
      return checkWechatInstalled;
    }
    if (
      methodName === 'openPageForDeerPictureBookModel' &&
      typeof openPageForDeerPictureBookModel !== 'undefined'
    ) {
      return openPageForDeerPictureBookModel;
    }
    if (methodName === 'openDeerDirectory' && typeof openDeerDirectory !== 'undefined') {
      return openDeerDirectory;
    }
    if (
      methodName === 'openExternalTeacherLessonVocabulary' &&
      typeof openExternalTeacherLessonVocabulary !== 'undefined'
    ) {
      return openExternalTeacherLessonVocabulary;
    }
    if (
      methodName === 'openExternalTeacherLessonWordGame' &&
      typeof openExternalTeacherLessonWordGame !== 'undefined'
    ) {
      return openExternalTeacherLessonWordGame;
    }
    if (
      methodName === 'openExternalTeacherLessonPhonicsPractice' &&
      typeof openExternalTeacherLessonPhonicsPractice !== 'undefined'
    ) {
      return openExternalTeacherLessonPhonicsPractice;
    }
    if (
      methodName === 'lessonInteractionNative' &&
      typeof lessonInteractionNative !== 'undefined'
    ) {
      return lessonInteractionNative;
    }
  } catch (_) {
    return undefined;
  }
  return undefined;
}

export function getMessageMethod(methodName: string): NativeMessageMethod | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const windowMethod = window[methodName as keyof Window] as
    | NativeMessageMethod
    | undefined;
  return windowMethod || getArkWebGlobalMethod(methodName);
}

function postNativeMessage(methodName: string, data?: any): boolean {
  const method = getMessageMethod(methodName);
  if (!method || typeof method.postMessage !== 'function') {
    return false;
  }
  try {
    method.postMessage(data);
    return true;
  } catch (_) {
    return false;
  }
}

type NativeDecryptCallback = (args: unknown) => void;
type NativeDecryptRequest = {
  data: string;
  callback: NativeDecryptCallback;
  onError?: (error: Error) => void;
};

const nativeDecryptQueue: NativeDecryptRequest[] = [];
let nativeDecryptInFlight = false;

/**
 * The App returns every decrypt result through the same global
 * `window.getDecryptData` callback. Only one request may therefore be in
 * flight at a time; otherwise a later request replaces the earlier callback
 * and leaves one of the callers pending forever.
 */
function processNativeDecryptQueue() {
  if (nativeDecryptInFlight || nativeDecryptQueue.length === 0) return;

  const request = nativeDecryptQueue[0];
  const method = getMessageMethod('nativeDecrypt');
  if (!method || typeof method.postMessage !== 'function') {
    nativeDecryptQueue.shift();
    request.onError?.(new Error('当前 App 不支持数据解密'));
    Toast.show({ content: '请升级APP到最新版本' });
    processNativeDecryptQueue();
    return;
  }

  nativeDecryptInFlight = true;
  window.getDecryptData = (decryptData: unknown) => {
    try {
      request.callback(decryptData);
      return decryptData;
    } finally {
      nativeDecryptQueue.shift();
      nativeDecryptInFlight = false;
      processNativeDecryptQueue();
    }
  };

  try {
    method.postMessage(request.data);
  } catch (cause) {
    nativeDecryptQueue.shift();
    nativeDecryptInFlight = false;
    request.onError?.(
      cause instanceof Error ? cause : new Error('App 数据解密调用失败')
    );
    processNativeDecryptQueue();
  }
}

export function stringifyPointBookNativePreview(
  data: IOpenPointBookNativePreview,
): string {
  const integerCoordinates: Array<{ marker: string; value: number }> = [];
  let markerIndex = 0;
  const serialized = JSON.stringify(data, (key, value: unknown) => {
    if (key !== 'box' || !Array.isArray(value)) {
      return value;
    }

    return value.map((coordinate) => {
      if (
        typeof coordinate !== 'number' ||
        !Number.isFinite(coordinate) ||
        !Number.isInteger(coordinate)
      ) {
        return coordinate;
      }

      // JSON.stringify 会把 45.0 输出为 45，Dart jsonDecode 随后会得到 int。
      // 先放入只供本次序列化使用的标记，完成后再恢复为合法的 45.0 数字字面量。
      const marker = `\u0000point_book_box_double_${markerIndex}\u0000`;
      markerIndex += 1;
      integerCoordinates.push({ marker, value: coordinate });
      return marker;
    });
  });

  return integerCoordinates.reduce(
    (result, { marker, value }) => result.replace(
      JSON.stringify(marker),
      `${value.toFixed(1)}`,
    ),
    serialized,
  );
}

const APP_BRIDGE_METHODS = [
  'getUserInfo',
  'closeWebview',
  'setNavigationBar',
  'gotoLogin',
  'updateAppVersion',
  'openWebPage',
  'openFilipinoAssistantChat',
  'checkWechatInstalled',
];

export const JsBridge = {
  // 获取用户信息
  isInApp: (): boolean => {
    return APP_BRIDGE_METHODS.some((methodName) => messageMethodIsSupport(methodName)) || isApp();
  },

  setNavigationBar: (data?: any) => {
    if (messageMethodIsSupport('setNavigationBar')) {
      window.setNavigationBar?.postMessage(data);
    }
  },

  openAlbumAndUpload: (data?: any) => {
    if (messageMethodIsSupport('openAlbumAndUpload')) {
      window.openAlbumAndUpload?.postMessage(data);
    }
  },
  // 打开文件选择
  openFileAndUpload: (data?: any) => {
    if (messageMethodIsSupport('openFileAndUpload')) {
      window.openFileAndUpload?.postMessage(data);
    }
  },

  // 获取用户信息
  getUserInfo: (data?: any) => {
    if (messageMethodIsSupport('getUserInfo')) {
      const payload = typeof data === 'string' ? data : JSON.stringify(data || {});
      window.getUserInfo?.postMessage(payload);
    }
  },

  // 跳转到登录页面
  gotoLogin: (data?: any) => {
    if (messageMethodIsSupport('gotoLogin')) {
      window.gotoLogin?.postMessage(data);
    }
  },

  // 关闭webview
  closeWebview: (data?: any) => {
    if (messageMethodIsSupport('closeWebview')) {
      window.closeWebview?.postMessage(data);
    }
  },

  // 设置全局函数，用户手势或者物理按键回退后的回调
  setClickBackCallback: (fun: any) => {
    window['clickBack'] = () => {
      try {
        fun();
      } catch (error) {
        return false;
      }
      return true;
    };
  },

  // native 解密接口返参
  nativeDecrypt: (
    data: string,
    callback: NativeDecryptCallback,
    onError?: (error: Error) => void,
  ) => {
    nativeDecryptQueue.push({ data, callback, onError });
    processNativeDecryptQueue();
  },

  shareUrl: (data?: any) => {
    if (messageMethodIsSupport('shareUrl')) {
      window.shareUrl?.postMessage(data);
    }
  },

  // 唤起APP分享
  shareWeb: (data?: any) => {
    if (messageMethodIsSupport('shareWeb')) {
      window.shareWeb?.postMessage(data);
    }
  },

  // APP内唤起微信客服
  openCustomerServiceChat: (data?: any) => {
    if (messageMethodIsSupport('openCustomerServiceChat')) {
      window.openCustomerServiceChat?.postMessage(data);
    }
  },

  // APP 内唤起菲教助教
  openFilipinoAssistantChat: (data?: any) => {
    return postNativeMessage('openFilipinoAssistantChat', data);
  },

  // 查询设备是否安装微信，结果由原生通过 window.postMessage 回传
  checkWechatInstalled: (data?: any) => {
    return postNativeMessage('checkWechatInstalled', data);
  },

  //会员下单
  buyVip: (data?: any) => {
    return postNativeMessage('buyVip', data);
  },
  // 直接使用微信支付参数调起原生微信支付
  wxPay: (data?: any) => {
    return postNativeMessage('wxPay', data);
  },
  //app升级
  updateAppVersion: (data?: any) => {
    if (messageMethodIsSupport('updateAppVersion')) {
      window.updateAppVersion?.postMessage(data);
    }
  },

  openWebPage: (data?: any) => {
    if (messageMethodIsSupport('openWebPage')) {
      window.openWebPage?.postMessage(data);
    }
  },

  sharePageFreeMembership: (data: string) => {
    if (messageMethodIsSupport('shareUrl')) {
      window.shareUrl?.postMessage(data);
    }
  },

  hideClientNavbar: (data: string) => {
    if (messageMethodIsSupport('setNavigationBar')) {
      window.setNavigationBar?.postMessage(data);
    }
  },

  wxLogin: (data: any) => {
    if (messageMethodIsSupport('wxLogin')) {
      window.wxLogin?.postMessage(data);
    }
  },
  // 打开绘本，视频等
  openPageForDeerPictureBookModel: (data: IOpenPageForDeerPictureBookModel) => {
    return postNativeMessage(
      'openPageForDeerPictureBookModel',
      JSON.stringify(data),
    );
  },
  // 后续 App 发版后可将管理员目录入口切换到该原生桥接。
  openDeerDirectory: (data: IOpenDeerDirectory) => {
    return postNativeMessage('openDeerDirectory', JSON.stringify(data));
  },
  offlineContentCommand: (data: IOfflineContentCommand): boolean => {
    return postNativeMessage('offlineContentCommand', JSON.stringify(data));
  },
  openPointBookNativePreview: (data: IOpenPointBookNativePreview): boolean => {
    if (!messageMethodIsSupport('openPointBookNativePreview')) {
      return false;
    }
    window.openPointBookNativePreview?.postMessage(
      stringifyPointBookNativePreview(normalizePointBookNativePreview(data)),
    );
    return true;
  },
  openExternalTeacherLessonVocabulary: (data: IOpenExternalTeacherLessonVocabulary): boolean => {
    return postNativeMessage(
      'openExternalTeacherLessonVocabulary',
      JSON.stringify(data),
    );
  },
  openExternalTeacherLessonWordGame: (data: IOpenExternalTeacherLessonWordGame): boolean => {
    return postNativeMessage(
      'openExternalTeacherLessonWordGame',
      JSON.stringify(data),
    );
  },
  openExternalTeacherLessonPhonicsPractice: (
    data: IOpenExternalTeacherLessonPhonicsPractice,
  ): boolean => {
    return postNativeMessage(
      'openExternalTeacherLessonPhonicsPractice',
      JSON.stringify(data),
    );
  },
  openAdminVocabularyPreview: (data: IOpenAdminVocabularyPreview): boolean => {
    if (!messageMethodIsSupport('openAdminVocabularyPreview')) {
      return false;
    }
    window.openAdminVocabularyPreview?.postMessage(JSON.stringify(data));
    return true;
  },
  lessonInteractionNative: (data: Record<string, unknown>): boolean => {
    return postNativeMessage('lessonInteractionNative', JSON.stringify(data));
  },
  openVideoAndUpload: (data?: any) => {
    if (messageMethodIsSupport('openVideoAndUpload')) {
      window.openVideoAndUpload?.postMessage(data);
    }
  },

  showContactServiceDialog: (data?: { sourceFrom: string }) => {
    if (messageMethodIsSupport('showContactServiceDialog')) {
      window.showContactServiceDialog?.postMessage(data);
    }
  },
};

// message 类的方法，直接挂在widow上, 只能调用APP方法，无法获取返回值
export function messageMethodIsSupport(methodName: string): boolean | undefined {
  return getMessageMethod(methodName) ? true : undefined;
}
